/**
 * dbService.js
 *
 * Thin API client that talks to the Express backend.
 * All functions are fire-and-forget safe — they never throw to callers.
 * localStorage remains the synchronous read-cache; the DB is the source of truth.
 */

const BASE = process.env.REACT_APP_API_URL || '/api';

// ── Ready gate — syncKey waits until initUser completes ───────────────────
let _readyResolve;
const _readyPromise = new Promise(resolve => { _readyResolve = resolve; });

// ── Prevent duplicate notification posts within a session ─────────────────
const _sentNotifFingerprints = new Set();

// ── User ID ────────────────────────────────────────────────────────────────
// Uses Google ID when available, falls back to a generated anonymous UUID.
export function getUserId() {
  let uid = localStorage.getItem('sl_user_id');
  if (!uid) {
    uid = 'anon_' + ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
    );
    localStorage.setItem('sl_user_id', uid);
  }
  return uid;
}

async function post(url, body) {
  return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
}
async function put(url, body) {
  return fetch(url, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
}

// ── Init / hydrate ─────────────────────────────────────────────────────────
/**
 * Called once on app startup.
 * 1. Ensures a user row exists in the DB.
 * 2. Fetches full snapshot and writes each key to localStorage.
 * Returns true if hydration succeeded.
 */
export async function initUser({ googleId, googleEmail, googlePicture, name, inviteToken } = {}) {
  try {
    const existingUid = localStorage.getItem('sl_user_id');
    const res  = await post(`${BASE}/users`, {
      id: existingUid || undefined,
      googleId, googleEmail, googlePicture, name,
      inviteToken: inviteToken || undefined,
    });
    const data = await res.json();
    if (data.id) localStorage.setItem('sl_user_id', data.id);
    const hydrateResult = await hydrateFromDB();
    if (hydrateResult && hydrateResult.sl_anon_expired) return { expired: true };
    return { data: hydrateResult || {} };
  } catch (err) {
    console.warn('[db] initUser failed (offline?):', err.message);
    return false;
  } finally {
    _readyResolve(); // always unblock syncKey, even on failure
  }
}

/** Returns true if the anonymous session has expired (caller should show expiry screen). */
export async function hydrateFromDB() {
  const uid = getUserId();
  try {
    const res  = await fetch(`${BASE}/users/${uid}`);
    if (!res.ok) return false;
    const data = await res.json();

    // Anonymous session expired — clear all sl_* data except user id
    if (data.sl_anon_expired) {
      for (const k of Object.keys(localStorage)) {
        if (k.startsWith('sl_') && k !== 'sl_user_id') localStorage.removeItem(k);
      }
      return { sl_anon_expired: true }; // signal expiry to caller
    }

    for (const [key, val] of Object.entries(data)) {
      if (val === undefined || val === null) continue;

      // Notifications from DB are already stored — mark them synced so syncKey
      // doesn't re-POST them when the app calls setNotifications after hydration.
      if (key === 'sl_notifs') {
        if (Array.isArray(val)) {
          localStorage.setItem(key, JSON.stringify(val.map(n => ({ ...n, _synced: true }))));
          // Pre-seed the sent-fingerprints Set so syncKey won't re-POST coach
          // notifications that already exist in DB (e.g. after logout+re-login).
          val.filter(n => n.type === 'coach').forEach(n => {
            _sentNotifFingerprints.add(`coach|${n.text}`);
          });
        }
        continue;
      }

      // Merge sleep/mood logs: combine server + local entries, prefer newer ts per date
      if (key === 'sl_sleep_log' || key === 'sl_mood_log') {
        if (Array.isArray(val)) {
          try {
            const local = JSON.parse(localStorage.getItem(key) || '[]');
            if (Array.isArray(local) && local.length > 0) {
              const merged = [...local];
              for (const sv of val) {
                const idx = merged.findIndex(e => e.date === sv.date);
                if (idx === -1) merged.push(sv);
                else if ((sv.ts || 0) > (merged[idx].ts || 0)) merged[idx] = sv;
              }
              merged.sort((a, b) => b.date.localeCompare(a.date));
              localStorage.setItem(key, JSON.stringify(merged));
              continue;
            }
          } catch {}
        }
      }

      // Merge exercise history: deduplicate by exId + minute bucket of date
      if (key === 'sl_ex_history') {
        if (Array.isArray(val)) {
          try {
            const local = JSON.parse(localStorage.getItem(key) || '[]');
            const dedupeKey = e => `${e.exId}|${e.catId}|${Math.floor((e.date || 0) / 60000)}`;
            const merged = [...local];
            const seenKeys = new Set(merged.map(dedupeKey));
            for (const sv of val) {
              const dk = dedupeKey(sv);
              if (!seenKeys.has(dk)) { merged.push(sv); seenKeys.add(dk); }
            }
            merged.sort((a, b) => (b.date || 0) - (a.date || 0));
            localStorage.setItem(key, JSON.stringify(merged));
          } catch {}
        }
        continue;
      }

      // Merge per-game score history (sl_hist_*)
      if (key.startsWith('sl_hist_')) {
        if (Array.isArray(val)) {
          try {
            const local = JSON.parse(localStorage.getItem(key) || '[]');
            const seenTs = new Set(local.map(e => e.ts));
            const merged = [...local];
            for (const sv of val) {
              if (!seenTs.has(sv.ts)) { merged.push(sv); seenTs.add(sv.ts); }
            }
            merged.sort((a, b) => (b.ts || 0) - (a.ts || 0));
            if (merged.length > 50) merged.length = 50;
            localStorage.setItem(key, JSON.stringify(merged));
          } catch {}
        }
        continue;
      }

      // For last_checkin: keep local if it is today or more recent
      if (key === 'sl_last_checkin') {
        const local = localStorage.getItem(key);
        const todayKey = new Date().toISOString().slice(0, 10);
        if (local && JSON.parse(local) >= todayKey) continue;
      }

      // Keep local name if already set — server may have a stale/default value
      if (key === 'sl_name') {
        if (!val) continue; // never overwrite with empty server value
        const local = localStorage.getItem(key);
        const localVal = local ? JSON.parse(local) : '';
        if (localVal && localVal !== 'Sense Lifter') continue;
      }

      // Google picture is stored as a raw URL (not JSON-encoded) everywhere else,
      // so write it raw here too to avoid "url" → '"url"' mismatch.
      if (key === 'sl_google_picture') {
        if (val) localStorage.setItem(key, val);
        continue;
      }

      // Auth method is read as a raw string in App.js (localStorage.getItem === 'email'),
      // so store it raw — not JSON-encoded — to avoid '"email"' !== 'email' mismatch.
      if (key === 'sl_auth_method') {
        if (typeof val === 'string') localStorage.setItem(key, val);
        continue;
      }

      // Business ID is always read with localStorage.getItem() (no JSON.parse) so it must
      // be stored raw — not JSON-encoded — to avoid '"id"' !== 'id' mismatches in API calls.
      if (key === 'sl_business_id') {
        if (val) localStorage.setItem(key, val);
        continue;
      }

      // Banner and avatar images are base64 data URLs — store raw, never JSON-encode.
      if (key === 'sl_banner_img') {
        if (val) localStorage.setItem(key, val);
        continue;
      }
      if (key === 'sl_avatar_img') {
        if (val) {
          localStorage.setItem(key, val);
          // Also write to the per-user key that AvatarUploader reads
          localStorage.setItem(`sl_avatar_${getUserId()}`, val);
        }
        continue;
      }

      // Don't overwrite existing non-empty local arrays with empty DB data.
      // This protects local-only data (e.g. posts, friends) when a user creates
      // a fresh account — the DB is empty but their local data is still valid.
      if (Array.isArray(val) && val.length === 0) {
        try {
          const existing = JSON.parse(localStorage.getItem(key) || 'null');
          if (Array.isArray(existing) && existing.length > 0) continue;
        } catch {}
      }

      // Don't revert sl_onboard_done from true → false (keeps completed-onboarding state
      // intact when mount hydration runs against a fresh anon account).
      if (key === 'sl_onboard_done' && val === false) {
        if (localStorage.getItem(key) === 'true') continue;
      }

      // Strip base64 images from posts before storing — they can easily exceed
      // the 5 MB localStorage quota. The img field is re-fetched from the DB when needed.
      if (key === 'sl_my_posts' && Array.isArray(val)) {
        const stripped = val.slice(0, 30).map(p => {
          const { img, ...rest } = p;
          return img ? { ...rest, img: null } : rest;
        });
        try { localStorage.setItem(key, JSON.stringify(stripped)); } catch {}
        continue;
      }

      try {
        localStorage.setItem(key, JSON.stringify(val));
      } catch (err) {
        console.warn('[db] hydrate setItem failed for', key, ':', err.message);
      }
    }
    return data; // return raw DB snapshot so callers can hydrate React state directly
  } catch (err) {
    console.warn('[db] hydrate failed (offline?):', err.message);
  }
  return null;
}

// ── Key → API sync map ─────────────────────────────────────────────────────
// Maps a localStorage key to the function that persists it to the DB.
const PROFILE_KEYS = new Set([
  'sl_name','sl_lang','sl_theme','sl_sound_theme','sl_banner_color','sl_accent_color',
  'sl_onboard_done','sl_last_checkin','sl_age',
]);

const KEY_FIELD_MAP = {
  sl_name:         'name',
  sl_lang:         'lang',
  sl_theme:        'theme',
  sl_sound_theme:  'sound_theme',
  sl_banner_color: 'banner_color',
  sl_accent_color: 'accent_color',
  sl_onboard_done: 'onboard_done',
  sl_last_checkin: 'last_checkin',
  sl_age:          'age',
};

/**
 * syncKey(key, value)
 * Called by useStorage on every write. Dispatches to the right endpoint.
 */
export async function syncKey(key, value) {
  await _readyPromise;
  const uid = getUserId();

  // Profile fields → PUT /api/users/:uid
  if (PROFILE_KEYS.has(key)) {
    const field = KEY_FIELD_MAP[key];
    put(`${BASE}/users/${uid}`, { [field]: value }).catch(() => {});
    return;
  }

  // Week plan
  if (key === 'sl_intention' || key === 'sl_agenda') {
    const intention = key === 'sl_intention' ? value : JSON.parse(localStorage.getItem('sl_intention') || '""');
    const agenda    = key === 'sl_agenda'    ? value : JSON.parse(localStorage.getItem('sl_agenda')    || '{}');
    put(`${BASE}/week/${uid}`, { intention, agenda }).catch(() => {});
    return;
  }

  // Sleep log — value is the full array; sync each entry not yet in DB
  if (key === 'sl_sleep_log') {
    if (!Array.isArray(value)) return;
    const today = new Date().toISOString().slice(0,10);
    const entry = value.find(e => e.date === today) || value[0];
    if (entry) {
      post(`${BASE}/sleep/${uid}`, entry).catch(() => {});
    }
    return;
  }

  // Mood log
  if (key === 'sl_mood_log') {
    if (!Array.isArray(value)) return;
    const today = new Date().toISOString().slice(0,10);
    const entry = value.find(e => e.date === today) || value[0];
    if (entry) {
      post(`${BASE}/mood/${uid}`, entry).catch(() => {});
    }
    return;
  }

  // Posts
  if (key === 'sl_my_posts') {
    if (!Array.isArray(value) || !value.length) return;
    const newest = value[0];
    // Only POST if the newest entry is a local (unsaved) post — identified by
    // having a large timestamp id (Date.now()) rather than a small DB integer id.
    const isLocalPost = newest && typeof newest.id === 'number' && newest.id > 1e12;
    if (isLocalPost && !newest._synced) {
      post(`${BASE}/posts/${uid}`, {
        text:     newest.text     || '',
        img:      newest.img      || null,
        isPublic: newest.isPublic !== false ? 1 : 0,
        feeling:  newest.feeling  || null,
      }).then(async r => {
        if (!r.ok) return;
        const data = await r.json();
        if (!data.id) return;
        // Replace the local Date.now() id with the real DB id and mark as synced.
        // This prevents the post from appearing twice after hydrateFromDB.
        try {
          const stored = JSON.parse(localStorage.getItem('sl_my_posts') || '[]');
          const updated = stored.map(p => p.id === newest.id ? { ...p, id: data.id, _synced: true } : p);
          localStorage.setItem('sl_my_posts', JSON.stringify(updated));
        } catch {}
      }).catch(() => {});
    }
    return;
  }

  // Notifications
  if (key === 'sl_notifs') {
    if (!Array.isArray(value)) return;
    const unread = value.filter(n => !n.read && !n._synced);
    unread.forEach(n => {
      const fp = `${n.type}|${n.text}`;
      if (_sentNotifFingerprints.has(fp)) return;
      _sentNotifFingerprints.add(fp);
      post(`${BASE}/notifs/${uid}`, { type: n.type, from: n.from, text: n.text }).catch(() => {});
    });
    return;
  }

  // Exercise history — sync the most-recent unsaved entry
  if (key === 'sl_ex_history') {
    if (!Array.isArray(value) || !value.length) return;
    const newest = value[0];
    if (newest && !newest._synced) {
      post(`${BASE}/exercise/${uid}`, newest).catch(() => {});
    }
    return;
  }

  // Growth levels — value is { catId: level, ... }
  if (key === 'sl_cat_levels') {
    if (typeof value !== 'object') return;
    Object.entries(value).forEach(([catId, level]) => {
      post(`${BASE}/growth/${uid}/${catId}`, { level, xp: 0 }).catch(() => {});
    });
    return;
  }

  // Conversations
  if (key === 'sl_convos') {
    // Conversations are complex; just persist via a batch endpoint if needed
    // For now we keep them localStorage-only (no dedicated table sync here)
    return;
  }
}

// ── Real-time social sync poll ─────────────────────────────────────────────
/**
 * pollSync(uid) — lightweight call that returns the latest social state:
 *   { notifications, friends, friend_requests, conversations }
 * Used by App.js for the unified 8-second poll loop.
 */
export async function pollSync(uid) {
  try {
    const res = await fetch(`${BASE}/sync/${uid}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

// ── Individual helpers used by specific screens ────────────────────────────

export async function syncSleepEntry(entry) {
  await _readyPromise;
  const uid = getUserId();
  post(`${BASE}/sleep/${uid}`, entry).catch(() => {});
}

export async function syncMoodEntry(entry) {
  await _readyPromise;
  const uid = getUserId();
  post(`${BASE}/mood/${uid}`, entry).catch(() => {});
}

export async function syncGameScore(gameId, score) {
  await _readyPromise;
  const uid = getUserId();
  post(`${BASE}/games/${uid}/${gameId}`, { score }).catch(() => {});
}

export async function syncGrowthLevel(categoryId, level, xp = 0) {
  await _readyPromise;
  const uid = getUserId();
  post(`${BASE}/growth/${uid}/${categoryId}`, { level, xp }).catch(() => {});
}

export async function syncFriendRequest(toId) {
  await _readyPromise;
  const uid = getUserId();
  try {
    const res = await post(`${BASE}/friends/${uid}/request`, { toId });
    const data = await res.json();
    return data.invite_token || null;
  } catch {
    return null;
  }
}

export async function syncFriendAccept(fromId) {
  await _readyPromise;
  const uid = getUserId();
  put(`${BASE}/friends/${uid}/request/${fromId}`, { status: 'accepted' }).catch(() => {});
}

export async function syncFriendDecline(fromId) {
  await _readyPromise;
  const uid = getUserId();
  put(`${BASE}/friends/${uid}/request/${fromId}`, { status: 'declined' }).catch(() => {});
}

// ── Push notifications ─────────────────────────────────────────────────────

/** Call any time — safely subscribes / re-subscribes to push notifications. */
export async function subscribeToPush() {
  await _readyPromise;
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    // Don't ask for permission here — caller should handle that; only proceed if granted
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const reg = await navigator.serviceWorker.ready;

    // Always fetch the current VAPID key so we can detect key mismatches
    const keyRes = await fetch(`${BASE}/push/vapid-public-key`);
    if (!keyRes.ok) return;
    const { key } = await keyRes.json();
    const keyBytes = urlBase64ToUint8Array(key);

    let sub = await reg.pushManager.getSubscription();

    if (sub) {
      // If the existing subscription was created with a different VAPID key,
      // unsubscribe and re-subscribe so the server can encrypt with the right key.
      const subKeyBuffer = sub.options?.applicationServerKey;
      if (subKeyBuffer) {
        const subKey = new Uint8Array(subKeyBuffer);
        const match = subKey.length === keyBytes.length && subKey.every((b, i) => b === keyBytes[i]);
        if (!match) {
          await sub.unsubscribe();
          sub = null;
        }
      }
    }

    if (!sub) {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes });
    }

    // Always (re-)register subscription with server to keep it fresh
    const uid = getUserId();
    const saveRes = await post(`${BASE}/push/${uid}/subscribe`, sub.toJSON());
    const saveData = await saveRes.json().catch(() => null);
    if (!saveRes.ok || !saveData?.ok) {
      console.warn('[push] subscribe save failed:', saveRes.status, saveData);
    }
  } catch (err) {
    console.warn('[push] subscribe failed:', err.message);
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

export async function fetchUsers(excludeUid) {
  try {
    const res = await fetch(`${BASE}/users?exclude=${encodeURIComponent(excludeUid || '')}`);
    if (!res.ok) return [];
    return await res.json();
  } catch {
    return [];
  }
}

export async function syncMarkNotifsRead() {
  await _readyPromise;
  const uid = getUserId();
  put(`${BASE}/notifs/${uid}`, {}).catch(() => {});
}

export async function syncReminderPrefs(offsetMins, tzOffsetMins) {
  await _readyPromise;
  const uid = getUserId();
  put(`${BASE}/users/${uid}`, { reminder_offset_mins: offsetMins, tz_offset_mins: tzOffsetMins }).catch(() => {});
}

export async function sendChatMessage(fromId, toId, text, time, type = 'text', audio = null, duration = null, img = null, extra = {}) {
  await _readyPromise;
  try {
    const body = { fromId, toId, text, time, type, ...extra };
    if (audio)    body.audio    = audio;
    if (duration) body.duration = duration;
    if (img)      body.img      = img;
    const res = await post(`${BASE}/chat/message`, body);
    return await res.json();
  } catch { return null; }
}

/** Mark all messages from partnerId as read (call when opening the conversation). */
export async function markChatRead(uid, partnerId) {
  try {
    await post(`${BASE}/chat/read`, { uid, partnerId });
  } catch {}
}

/** Delete a single chat message (only the sender can delete). */
export async function deleteChatMessage(fromId, toId, msgId) {
  await _readyPromise;
  return fetch(`${BASE}/chat/message`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fromId, toId, msgId }),
  }).catch(() => {});
}

export async function saveChatReaction(fromId, toId, msgId, emoji) {
  try {
    await fetch(`${BASE}/chat/reaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fromId, toId, msgId, emoji }),
    });
  } catch {}
}

/**
 * Toggle a like on a post.
 * Returns { likes: number, liked: boolean } or null on failure.
 */
export async function togglePostLike(postId) {
  await _readyPromise;
  const uid = getUserId();
  try {
    const res = await post(`${BASE}/posts/${uid}/${postId}/like`, {});
    return await res.json();
  } catch {
    return null;
  }
}

/** Share/repost a post. Returns { ok, id } or null on failure. */
export async function sharePostApi(postId, text = '') {
  await _readyPromise;
  const uid = getUserId();
  try {
    const res = await post(`${BASE}/posts/${uid}/share`, { post_id: postId, text, isPublic: 1 });
    return await res.json();
  } catch {
    return null;
  }
}

/** Fetch all conversations for the current user from the DB. */
export async function fetchConversations() {
  await _readyPromise;
  const uid = getUserId();
  try {
    const res = await fetch(`${BASE}/chat/${uid}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export async function deletePost(postId) {
  await _readyPromise;
  const uid = getUserId();
  return fetch(`${BASE}/posts/${uid}/${postId}`, { method: 'DELETE' }).catch(() => {});
}

export async function notifyUser(userId, type, fromName, text, meta = null) {
  await _readyPromise;
  const body = { type, from: fromName, text };
  if (meta) body.meta = meta;
  post(`${BASE}/notifs/${userId}`, body).catch(() => {});
}

export async function syncExerciseEntry(entry) {
  await _readyPromise;
  const uid = getUserId();
  post(`${BASE}/exercise/${uid}`, entry).catch(() => {});
}

// ── Social stats ───────────────────────────────────────────────────────────────

/**
 * Log a social interaction event.
 * type: 'post_like' | 'post_comment' | 'post_created' | 'exercise_partner' |
 *       'group_exercise' | 'game_together' | 'group_chat' | 'friend_accept' | 'group_admin'
 * targetId: the other user's id (for connection strength calculations)
 * groupId: the group id (optional)
 */
export async function trackSocialEvent(type, { targetId = null, groupId = null, weight = 1 } = {}) {
  await _readyPromise;
  const uid = getUserId();
  post(`${BASE}/social/${uid}/event`, { type, targetId, groupId, weight }).catch(() => {});
}

/** Fetch social skill scores for a user. Returns { skills, uniquePartners, topConnections, eventCounts } */
export async function getSocialStats(userId) {
  try {
    const uid = userId || getUserId();
    const res = await fetch(`${BASE}/social/${uid}/stats`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/** Save a hero banner image (base64) to the DB for the current user. */
export async function saveBannerImage(base64) {
  await _readyPromise;
  const uid = getUserId();
  put(`${BASE}/users/${uid}`, { banner_img: base64 }).catch(() => {});
}

/** Save a profile avatar image (base64) to the DB for the current user. */
export async function saveAvatarImage(base64) {
  await _readyPromise;
  const uid = getUserId();
  put(`${BASE}/users/${uid}`, { avatar_img: base64 }).catch(() => {});
}

/**
 * Create a game session invite.
 * Returns the invite token, or null on failure.
 */
export async function createGameSession(gameTitle, mode, inviterId, inviterName, inviteeId = null) {
  await _readyPromise;
  const uid = inviterId || getUserId();
  try {
    const res = await post(`${BASE}/game-sessions`, { gameTitle, mode, inviterId: uid, inviterName, inviteeId });
    const data = await res.json();
    return data.token || null;
  } catch {
    return null;
  }
}

/**
 * Join a game session as the invited friend.
 * Returns full session state or null on failure.
 */
export async function joinGameSession(token, joinerId, joinerName) {
  try {
    const res = await post(`${BASE}/game-sessions/${token}/join`, { joinerId, joinerName });
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Mark the current user as ready in a game session.
 * Returns full session state or null on failure.
 */
export async function setGameSessionReady(token, userId, role) {
  try {
    const res = await fetch(`${BASE}/game-sessions/${token}/ready`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, role }),
    });
    return await res.json();
  } catch {
    return null;
  }
}

/** Push shared game state for multiplayer sync */
export async function updateGameState(token, state) {
  await _readyPromise;
  return put(`${BASE}/game-sessions/${token}/state`, { state }).catch(() => {});
}

/**
 * Fetch game session details by token.
 * Returns { gameTitle, inviterName, mode, status } or null.
 */
export async function getGameSession(token) {
  try {
    const res = await fetch(`${BASE}/game-sessions/${token}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/** Stop (cancel) a game session. */
export async function stopGameSession(token) {
  try {
    return put(`${BASE}/game-sessions/${token}/stop`, {});
  } catch {
    return null;
  }
}

/** Mark a game invite as seen by the invitee (popup was shown). */
export async function markGameInviteSeen(token) {
  try { await fetch(`${BASE}/game-sessions/${token}/seen`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: '{}' }); } catch {}
}

/** Mark a game invite as declined by the invitee. */
export async function declineGameInvite(token) {
  try { await fetch(`${BASE}/game-sessions/${token}/decline`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: '{}' }); } catch {}
}

/** Permanently delete a user account and all associated data. */
export async function deleteAccount(userId) {
  const res = await fetch(`${BASE}/users/${encodeURIComponent(userId)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Delete failed');
  return res.json();
}

/** Fetch active game sessions and pending invitations for a user. */
export async function getActiveSessions(userId) {
  try {
    const res = await fetch(`${BASE}/game-sessions?userId=${encodeURIComponent(userId)}`);
    if (!res.ok) return { sessions: [], pending: [] };
    return await res.json();
  } catch {
    return { sessions: [], pending: [] };
  }
}

/** Permanently delete a single notification from the DB. */
export async function deleteNotification(notifId) {
  await _readyPromise;
  const uid = getUserId();
  fetch(`${BASE}/notifs/${uid}/${notifId}`, { method: 'DELETE' }).catch(() => {});
}

/** Set or clear a rest moment. minutes=0 clears. Returns restUntil ISO string or null. */
export async function setRestUntil(minutes) {
  await _readyPromise;
  const uid = getUserId();
  try {
    const res = await fetch(`${BASE}/users/${uid}/rest`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ minutes }),
    });
    if (res.ok) return (await res.json()).restUntil || null;
  } catch {}
  return null;
}

/** Fetch a user's full hydration data (includes sl_my_posts with img, sl_banner_img). */
export async function fetchUserProfile(userId) {
  try {
    const res = await fetch(`${BASE}/users/${userId}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/** Fetch stability + social badges for a user profile. */
export async function getUserBadges(userId) {
  try {
    const uid = userId || getUserId();
    const res = await fetch(`${BASE}/profile/${uid}/badges`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/** Update the current user's last_seen timestamp on the server. */
export async function sendHeartbeat() {
  const uid = getUserId();
  if (!uid) return;
  put(`${BASE}/users/${uid}/heartbeat`, {}).catch(() => {});
}

/** Unsubscribe from push: remove PushManager subscription + delete from DB. */
export async function unsubscribeFromPush() {
  await _readyPromise;
  try {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
    }
  } catch {}
  try {
    const uid = getUserId();
    if (uid) fetch(`${BASE}/push/${uid}/subscribe`, { method: 'DELETE' }).catch(() => {});
  } catch {}
}

/** Low-level helper: trigger server push for the current user.
 *  The server delivers the push via VAPID/FCM; the SW push handler shows the notification.
 *  This avoids the double-notification bug where both a local showNotification() and
 *  the SW push handler would fire simultaneously. */
async function _pushNotify(title, body, url = '/') {
  await _readyPromise;

  // Soft-disable: user has turned off push in app settings
  if (localStorage.getItem('sl_push_enabled') === 'false') return;

  const uid = getUserId();

  // Server-sent push only — SW push handler shows the notification.
  // Do NOT call reg.showNotification() here: that would create a duplicate
  // whenever the server push also arrives (2 notifications for 1 event).
  post(`${BASE}/push/${uid}/onboarding-notify`, { title, body, url }).catch(() => {});
}

/** Public push helper — same as _pushNotify but exported for general use. */
export async function sendPushReminder(title, body, url = '/') {
  return _pushNotify(title, body, url);
}

export async function sendOnboardingPushNotif(title, body, url = '/') {
  return _pushNotify(title, body, url);
}

/** Fetch connection strength score (0–100) between two users. */
export async function getConnectionStrength(uid1, uid2) {
  try {
    const res = await fetch(`${BASE}/social/connection/${uid1}/${uid2}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}
